import React from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import ProtectedRoute from './components/ProtectedRoute';
import ClientsContextProvider from './contexts/ClientsContext';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Client from './pages/Client';
import ContentStrategySettings from './pages/ContentStrategySettings';
import ClientImages from './pages/ClientImages';
import ClientReviews from './pages/ClientReviews';
import ClientGoogleProfile from './pages/ClientGoogleProfile';
import Groups from './pages/Groups';
import Reviews from './pages/Reviews';
import ReviewsV2 from './pages/ReviewsV2';
import ReviewsSettings from './pages/Reviews/ReviewsSettings';
import RequestReviews from './pages/Reviews/RequestReviews';
import AllReviews from './pages/AllReviews';
import AuditLog from './pages/AuditLog';
import EventLogs from './pages/eventLogs';
import ContentStrategies from './pages/ContentStrategies';
import Images from './pages/Images';
import WebsiteSEOAudit from './pages/WebsiteSEOAudit';
import WebsiteSEOAuditV2 from './pages/WebsiteSEOAuditV2';
import WebsiteSEOAuditV3 from './pages/WebsiteSEOAuditV3';
import SeoAuditSettings from './pages/SeoAuditSettings';
import SeoPublicReport from './pages/SeoPublicReport';
import ListingManagement from './pages/ListingManagement';
import LocalRankTracker from './pages/LocalRankTracker';
import RankTrackerDashboard from './pages/RankTracker/index.tsx';
import Reports from './pages/Reports';
import {
  AssignmentsMain,
  AssignmentMonthlyReport,
  AssignmentDailyReport
} from './pages/AssignmentPage';
import Notifications from './pages/Notifications';
import Mobile from './components/AppRegister/Mobile';
import NotFound from './pages/NotFound';
import NewClient from './components/NewClientOnboarding';
import GoogleBusinessProfileSection from './pages/Client/GoogleBusinessProfileSection/GoogleBusinessProfileSection';
import {
  LoginPage,
  SignUpPage,
  ForgotPasswordPage,
  SignUpContinuePage
} from './pages/Login';
import GoogleCallback from './pages/OAuthCallback/GoogleCallback';
import AppRegisterPage from './pages/AppRegister';
import AppRegisterPageNew from './pages/AppRegister_new';

import {
  SEO_AUDIT_ENABLED,
  LOCAL_RANK_TRACKER_ENABLED,
  REPORTS_ENABLED,
  CLIENTS_LIST_ENABLED
} from './utils/constants';
import FacebookCallback from './pages/OAuthCallback/FacebookCallback';
import FacebookListingCallback from './pages/OAuthCallback/FacebookListingCallback';
import FoursquareCallback from './pages/OAuthCallback/FoursquareCallback';
import GbpCallback from './pages/OAuthCallback/GbpCallback';
import BingCallback from './pages/OAuthCallback/BingCallback';
import TikTokCallback from './pages/OAuthCallback/TikTokCallback';
import NewPost from './pages/NewPost';
import ChatbaseWidget from './components/ChatbaseWidget';
import MobileEntry from './components/AppRegister/MobileEntry';
import EntriDomain from './pages/EntriDomain';

import Onboarding from './pages/Onboarding';

function AppContent({ isAuthenticated }) {
  const location = useLocation();

  return (
    <>
      <ChatbaseWidget
        isAuthenticated={isAuthenticated && location.pathname !== '/onboarding'}
      />
      <Switch>
        {/* Public Routes */}
        <Route exact path="/sign-in" component={LoginPage} />
        <Route exact path="/sign-up" component={SignUpPage} />
        <Route exact path="/sign-up/continue" component={SignUpContinuePage} />
        <Route exact path="/forgot-password" component={ForgotPasswordPage} />
        <Route exact path="/seo-report/:reportId" component={SeoPublicReport} />
        <Route exact path="/mobile-entry" component={MobileEntry} />
        <Route exact path="/domain" component={EntriDomain} />
        {/* OAuth Callback Routes */}
        <Route exact path="/oauth/callback/google" component={GoogleCallback} />
        <Route
          exact
          path="/oauth/callback/facebook"
          component={FacebookCallback}
        />
        <Route
          exact
          path="/facebook-listing-callback"
          component={FacebookListingCallback}
        />
        <Route
          exact
          path="/foursquare-callback"
          component={FoursquareCallback}
        />
        <Route exact path="/gbp-callback" component={GbpCallback} />
        <Route exact path="/bing-callback" component={BingCallback} />
        <Route exact path="/tiktok/callback" component={TikTokCallback} />

        {/* Protected Routes */}
        <ProtectedRoute
          exact
          path="/onboarding"
          component={Onboarding}
          hideLayout={true}
        />
        {CLIENTS_LIST_ENABLED === 'true' && (
          <ProtectedRoute
            exact
            path="/clients"
            component={Dashboard}
            requiresAdminDomain={true}
          />
        )}

        {/* {SEO_AUDIT_ENABLED === 'true' && (
          <ProtectedRoute exact path="/seo-audit" component={WebsiteSEOAudit} />
        )} */}

        <ProtectedRoute exact path="/seo-audit" component={WebsiteSEOAuditV3} />

        {SEO_AUDIT_ENABLED === 'true' && (
          <ProtectedRoute
            exact
            path="/seo-audit-v2"
            component={WebsiteSEOAuditV2}
          />
        )}
        {SEO_AUDIT_ENABLED === 'true' && (
          <ProtectedRoute
            exact
            path="/seo-audit/settings"
            component={SeoAuditSettings}
          />
        )}
        <ProtectedRoute exact path="/calendar" component={Calendar} />
        <ProtectedRoute
          exact
          path="/listing-management"
          component={ListingManagement}
        />
        {LOCAL_RANK_TRACKER_ENABLED === 'true' && (
          <ProtectedRoute
            exact
            path="/rank-tracker"
            component={RankTrackerDashboard}
          />
        )}
        {REPORTS_ENABLED === 'true' && (
          <ProtectedRoute exact path="/reports" component={Reports} />
        )}
        <ProtectedRoute exact path="/clientImages" component={ClientImages} />
        <ProtectedRoute exact path="/clientReviews" component={ClientReviews} />
        <ProtectedRoute
          exact
          path="/all-reviews"
          component={AllReviews}
          requiresAdminDomain={true}
        />
        <ProtectedRoute
          exact
          path="/clientGoogleProfile"
          component={ClientGoogleProfile}
        />
        <ProtectedRoute exact path="/mobile/" component={Mobile} />
        <ProtectedRoute exact path="/mobile-entry" component={MobileEntry} />
        <ProtectedRoute exact path="/client" component={Client} />

        <ProtectedRoute
          exact
          path="/ContentStrategySettings"
          component={ContentStrategySettings}
        />
        <ProtectedRoute exact path="/groups" component={Groups} />
        <ProtectedRoute exact path="/reviews" component={Reviews} />
        <ProtectedRoute exact path="/reviews-v2" component={ReviewsV2} />
        <ProtectedRoute
          exact
          path="/reviews-settings"
          component={ReviewsSettings}
        />
        <ProtectedRoute
          exact
          path="/request-reviews"
          component={RequestReviews}
        />
        <ProtectedRoute exact path="/audit-log" component={AuditLog} />
        <ProtectedRoute exact path="/logs" component={EventLogs} />
        {/* Assignments Routes */}
        <ProtectedRoute exact path="/assignment" component={AssignmentsMain} />
        <ProtectedRoute
          exact
          path="/assignment/monthly/:month/:year"
          component={AssignmentMonthlyReport}
        />
        <ProtectedRoute
          exact
          path="/assignment/daily/:contentCreator/:date"
          component={AssignmentDailyReport}
        />
        <ProtectedRoute
          exact
          path="/assignment/daily/:date"
          component={AssignmentDailyReport}
        />
        {/* Notifications Routes */}
        <ProtectedRoute
          exact
          path="/notifications/"
          component={Notifications}
        />
        <ProtectedRoute
          exact
          path="/notifications/:notificationId"
          component={Notifications}
        />
        <ProtectedRoute
          exact
          path="/content-strategies"
          component={ContentStrategies}
        />
        <ProtectedRoute exact path="/images" component={Images} />
        <ProtectedRoute
          exact
          path="/google-business"
          component={GoogleBusinessProfileSection}
        />
        <ProtectedRoute
          exact
          path="/new-client-onboarding"
          component={NewClient}
          requiresAdminDomain={true}
        />
        <ProtectedRoute exact path="/new-post" component={NewPost} />
        <ProtectedRoute
          exact
          path="/app-settings"
          requiresAdminDomain={true}
          component={AppRegisterPage}
        />
        <ProtectedRoute
          exact
          path="/app-register-new"
          component={AppRegisterPageNew}
        />
        <ProtectedRoute exact path="/" component={Client} />
        <Route path="*" component={NotFound} />
      </Switch>
    </>
  );
}

function App(props) {
  const { isSignedIn } = useAuth();

  const script = document.createElement('script');
  script.src = '../../../assets/js/dashforge.js';
  script.type = 'text/javascript';
  script.async = true;
  document.body.appendChild(script);

  return (
    <ClientsContextProvider>
      <AppContent isAuthenticated={isSignedIn === true} />
    </ClientsContextProvider>
  );
}

export default App;
